import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  BanknotesIcon,
  ChartBarIcon,
  XCircleIcon,
  EyeIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ArrowPathIcon,
  SunIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import reportService from '../../services/reportService';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, StatCard, Table, Modal, Loading } from '../common';

const DailyClosingReport = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingOpening, setSavingOpening] = useState(false);
  const [closingReports, setClosingReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Enhanced closing state
  const [currentBalances, setCurrentBalances] = useState(null);
  const [conversionRate, setConversionRate] = useState(1480);
  const [closingNotes, setClosingNotes] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const fetchClosingReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.getClosingReports();
      setClosingReports(data.reports || data.data || data || []);
    } catch (err) {
      console.error('Failed to fetch closing reports:', err);
      setError(t('reports.closingFetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchCurrentBalances = useCallback(async () => {
    try {
      const data = await reportService.getCurrentBalances(selectedDate);
      setCurrentBalances(data.data);
    } catch (err) {
      console.error('Failed to fetch current balances:', err);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchClosingReports();
  }, [fetchClosingReports]);

  useEffect(() => {
    if (selectedDate) {
      fetchCurrentBalances();
    }
  }, [selectedDate, fetchCurrentBalances]);

  const handleSaveOpeningSnapshot = async () => {
    setSavingOpening(true);
    setError(null);
    setSuccess(null);
    try {
      await reportService.saveOpeningSnapshot(selectedDate);
      setSuccess(t('reports.openingSnapshotSaved', 'Opening snapshot saved successfully'));
      fetchCurrentBalances();
    } catch (err) {
      console.error('Failed to save opening snapshot:', err);
      setError(err.response?.data?.message || t('reports.openingSnapshotError', 'Failed to save opening snapshot'));
    } finally {
      setSavingOpening(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await reportService.generateEnhancedClosingReport(
        selectedDate,
        conversionRate,
        closingNotes
      );
      setClosingReports(prev => [result.data || result, ...prev]);
      setSelectedReport(result.data || result);
      setDetailModalOpen(true);
      setShowPreview(false);
      setSuccess(t('reports.closingGenerateSuccess', 'Daily closing report generated successfully'));
    } catch (err) {
      console.error('Failed to generate closing report:', err);
      setError(err.response?.data?.message || t('reports.closingGenerateError'));
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReport = async (uuid) => {
    setLoading(true);
    try {
      const report = await reportService.getClosingReport(uuid);
      setSelectedReport(report.data || report);
      setDetailModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch closing report:', err);
      setError(t('reports.closingFetchError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount, currency = 'IQD') => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount) + ' ' + currency;
  };

  // Calculate totals from current balances
  const calculateTotals = () => {
    if (!currentBalances) return { cashUsd: 0, cashIqd: 0, cardUsd: 0, cardIqd: 0 };

    let cashUsd = 0;
    let cashIqd = 0;
    let cardUsd = 0;
    let cardIqd = 0;

    currentBalances.cashDrawers?.forEach(drawer => {
      cashUsd += drawer.currentBalances?.USD || 0;
      cashIqd += drawer.currentBalances?.IQD || 0;
    });

    currentBalances.cardAccounts?.forEach(account => {
      if (account.currency === 'USD') {
        cardUsd += account.balance;
      } else {
        cardIqd += account.balance;
      }
    });

    const totalIqd = cashIqd + cardIqd + (cashUsd + cardUsd) * conversionRate;

    return { cashUsd, cashIqd, cardUsd, cardIqd, totalIqd };
  };

  const totals = calculateTotals();

  const closingColumns = [
    {
      header: t('reports.date'),
      accessor: 'reportDate',
      render: (value) => (
        <div className="flex items-center">
          <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
          {formatDate(value)}
        </div>
      )
    },
    {
      header: t('reports.totalTransactions'),
      accessor: 'totalTransactions',
      render: (value) => value?.toLocaleString() || '0'
    },
    {
      header: t('reports.totalProfit'),
      accessor: 'totalProfit',
      render: (value) => (
        <span className={value >= 0 ? 'text-green-600 dark:text-green-400 font-medium' : 'text-red-600 dark:text-red-400 font-medium'}>
          {formatCurrency(value || 0)}
        </span>
      )
    },
    {
      header: t('reports.generatedBy'),
      accessor: 'generatedBy',
      render: (value) => (
        <div className="flex items-center">
          <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
          {value || '-'}
        </div>
      )
    },
    {
      header: t('common.actions'),
      accessor: 'uuid',
      render: (value) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => handleViewReport(value)}
        >
          <EyeIcon className="h-4 w-4 mr-1" />
          {t('common.view')}
        </Button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Generate Report Card */}
      <Card>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.selectDate')}
              </label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="input-field pl-10"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reports.conversionRate', 'USD to IQD Rate')}
              </label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="number"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(Number(e.target.value))}
                  className="input-field pl-10"
                  min="1"
                  step="1"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleSaveOpeningSnapshot}
                loading={savingOpening}
                disabled={savingOpening}
              >
                <SunIcon className="h-5 w-5 mr-2" />
                {t('reports.saveOpening', 'Save Opening')}
              </Button>

              <Button
                variant="secondary"
                onClick={() => setShowPreview(!showPreview)}
              >
                <EyeIcon className="h-5 w-5 mr-2" />
                {showPreview ? t('common.hide') : t('reports.preview', 'Preview')}
              </Button>

              {isAdmin() && (
                <Button
                  onClick={handleGenerateReport}
                  loading={generating}
                  className="flex items-center"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  {t('reports.generateClosing')}
                </Button>
              )}
            </div>
          </div>

          {/* Notes field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reports.notes', 'Notes')} ({t('common.optional')})
            </label>
            <textarea
              value={closingNotes}
              onChange={(e) => setClosingNotes(e.target.value)}
              className="input-field"
              rows={2}
              placeholder={t('reports.notesPlaceholder', 'Add any notes for this closing report...')}
            />
          </div>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center text-red-700 dark:text-red-400">
              <XCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center text-green-700 dark:text-green-400">
              <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              {success}
            </div>
          )}
        </div>
      </Card>

      {/* Current Balances Preview */}
      {showPreview && currentBalances && (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard
              title={t('reports.cashUsd', 'Cash USD')}
              value={formatCurrency(totals.cashUsd, t('common.usd'))}
              icon={BanknotesIcon}
            />
            <StatCard
              title={t('reports.cashIqd', 'Cash IQD')}
              value={formatCurrency(totals.cashIqd, t('common.iqd'))}
              icon={BanknotesIcon}
            />
            <StatCard
              title={t('reports.cardUsd', 'Card USD')}
              value={formatCurrency(totals.cardUsd, t('common.usd'))}
              icon={CreditCardIcon}
            />
            <StatCard
              title={t('reports.cardIqd', 'Card IQD')}
              value={formatCurrency(totals.cardIqd, t('common.iqd'))}
              icon={CreditCardIcon}
            />
            <StatCard
              title={t('reports.totalIqd', 'Total (IQD)')}
              value={formatCurrency(totals.totalIqd, t('common.iqd'))}
              icon={ChartBarIcon}
              className="col-span-2 md:col-span-1"
            />
          </div>

          {/* Cash Drawers */}
          <Card title={t('reports.cashDrawers', 'Cash Drawers')}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('reports.drawer', 'Drawer')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('common.usd')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('common.iqd')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('reports.hasOpening', 'Opening')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentBalances.cashDrawers?.map((drawer) => (
                    <tr key={drawer.uuid}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {drawer.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                        {formatCurrency(drawer.currentBalances?.USD || 0, t('common.usd'))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700 dark:text-gray-300">
                        {formatCurrency(drawer.currentBalances?.IQD || 0, t('common.iqd'))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {drawer.hasOpeningSnapshot ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500 inline" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Card Accounts */}
          <Card title={t('nav.cardTransfers')}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('cardTransfers.account')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('cardTransfers.provider')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('common.status')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentBalances.cardAccounts?.map((account) => (
                    <tr key={account.uuid}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {account.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${account.provider === 'FIB'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          }`}>
                          {account.provider}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(account.balance, account.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Card Transfer Summary */}
          {currentBalances.cardTransferSummary && (
            <Card title={t('reports.cardTransfers')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(currentBalances.cardTransferSummary).map(([provider, summary]) => (
                  <div key={provider} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                      <CreditCardIcon className="h-5 w-5 mr-2 text-primary-500" />
                      {provider}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('reports.incoming', 'Incoming')}:</span>
                        <span className="text-green-600 dark:text-green-400">
                          {summary.incomingCount} ({formatCurrency(summary.incoming, t('common.iqd'))})
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('reports.outgoing', 'Outgoing')}:</span>
                        <span className="text-red-600 dark:text-red-400">
                          {summary.outgoingCount} ({formatCurrency(summary.outgoing, t('common.iqd'))})
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">{t('reports.feesCollected', 'Fees (Profit)')}:</span>
                        <span className="text-primary-600 dark:text-primary-400 font-bold">
                          {formatCurrency(summary.incomingFees + summary.outgoingFees, t('common.iqd'))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total Fees */}
              <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {t('reports.totalCardFees', 'Total Card Fees (Profit)')}:
                  </span>
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(currentBalances.totalCardFees || 0, t('common.iqd'))}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {/* Transaction Summary */}
          {currentBalances.transactionSummary && (
            <Card title={t('reports.transactionSummary', 'Exchange Transactions Today')}>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {currentBalances.transactionSummary.count}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t('reports.transactions', 'Transactions')}
                  </div>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(currentBalances.transactionSummary.profit, t('common.iqd'))}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t('reports.exchangeProfit', 'Exchange Profit')}
                  </div>
                </div>
                <div className="text-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(
                      currentBalances.transactionSummary.profit + (currentBalances.totalCardFees || 0),
                      t('common.iqd')
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t('reports.totalProfit', 'Total Profit')}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Closing Reports History */}
      <Card
        title={t('reports.closingHistory')}
        action={
          <Button variant="secondary" size="sm" onClick={fetchClosingReports} loading={loading}>
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            {t('common.refresh')}
          </Button>
        }
      >
        {loading && closingReports.length === 0 ? (
          <Loading size="lg" className="py-12" />
        ) : (
          <Table
            columns={closingColumns}
            data={closingReports}
            loading={loading}
            emptyMessage={t('reports.noClosingReports')}
          />
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={t('reports.closingReport')}
        size="xl"
      >
        {selectedReport && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
            {/* Report Header */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center">
                  <CalendarIcon className="h-6 w-6 text-primary-500 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.date')}</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatDate(selectedReport.reportDate || selectedReport.date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-6 w-6 text-primary-500 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.currencyCalculator.currentRate')}</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {selectedReport.conversionRate?.toLocaleString() || '1,480'} {t('common.iqd')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <UserIcon className="h-6 w-6 text-primary-500 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.generatedBy')}</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {selectedReport.generatedBy || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Profit Breakdown */}
            {selectedReport.profitBreakdown && (
              <div className="grid grid-cols-3 gap-4">
                <StatCard
                  title={t('reports.exchangeProfit', 'Exchange Profit')}
                  value={formatCurrency(selectedReport.profitBreakdown.exchangeProfit)}
                  icon={BanknotesIcon}
                />
                <StatCard
                  title={t('reports.cardFees', 'Card Fees')}
                  value={formatCurrency(selectedReport.profitBreakdown.cardFees)}
                  icon={CreditCardIcon}
                />
                <StatCard
                  title={t('reports.totalProfit', 'Total Profit')}
                  value={formatCurrency(selectedReport.profitBreakdown.totalProfit)}
                  icon={ChartBarIcon}
                />
              </div>
            )}

            {/* Balance Summary */}
            {selectedReport.balanceSummary && (
              <Card title={t('reports.dailyClosing')}>
                <div className="space-y-4">
                  {/* Cash Drawers */}
                  {selectedReport.balanceSummary.cashDrawers?.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('nav.cashDrawers')}
                      </h5>
                      <div className="space-y-2">
                        {selectedReport.balanceSummary.cashDrawers.map((drawer, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-600 dark:text-gray-400">{drawer.name}</span>
                            <div className="text-right">
                              {drawer.balances?.USD && (
                                <span className="mr-4">{formatCurrency(drawer.balances.USD, 'USD')}</span>
                              )}
                              {drawer.balances?.IQD && (
                                <span>{formatCurrency(drawer.balances.IQD, 'IQD')}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card Accounts */}
                  {selectedReport.balanceSummary.cardAccounts?.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('nav.cardTransfers')}
                      </h5>
                      <div className="space-y-2">
                        {selectedReport.balanceSummary.cardAccounts.map((account, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                            <span className="text-gray-600 dark:text-gray-400">
                              {account.name} ({account.provider})
                            </span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatCurrency(account.balance, account.currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Grand Total */}
                  <div className="pt-4 border-t-2 border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {t('reports.total')} (IQD)
                      </span>
                      <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                        {formatCurrency(selectedReport.balanceSummary.grandTotalIqd)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Card Transfers */}
            {selectedReport.cardTransfers?.length > 0 && (
              <Card title={t('reports.cardTransfers')}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.provider')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.direction')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.count')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.amount')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.fees')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedReport.cardTransfers.map((transfer, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">{transfer.provider}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm capitalize">{t(`cardTransfers.${transfer.direction}`)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{transfer.count}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{formatCurrency(transfer.totalAmount)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(transfer.totalFees)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Currency Breakdown */}
            {selectedReport.currencyBreakdown?.length > 0 && (
              <Card title={t('reports.currencyBreakdown')}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.pair')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.count')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.in')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.out')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('reports.profit')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedReport.currencyBreakdown.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{item.pair}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{item.count}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{item.totalIn?.toLocaleString()}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{item.totalOut?.toLocaleString()}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(item.profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Employee Breakdown */}
            {selectedReport.employeeBreakdown?.length > 0 && (
              <Card title={t('reports.employeeBreakdown')}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('reports.employee')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('reports.transactions')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('reports.profit')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedReport.employeeBreakdown.map((emp, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{emp.name}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right">{emp.transactionCount}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(emp.profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DailyClosingReport;
